import { useCustomer } from "@/src/provider/Customer/CustomerProvider";
import TermsOfServiceUpdateDetails from "./TermsOfServiceUpdateDetails";
import PrivacyPolicyUpdateDetails from "./PrivacyPolicyUpdateDetails";
import { Box, Typography } from "@mui/material";
import React, { ReactNode, useState } from "react";
import MuiListNativeStyled from "@/src/components/List/MuiListNativeStyled";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import ButtonLoading from "@/src/components/Button/ButtonLoading";
import DialogModal from "@/src/components/DialogModal/DialogModal";
import dayjs from "dayjs";

const ConsentUpdateModal = () => {
  const TERMS_OF_SERVICE_UPDATED_DATE = "26.05.2023";
  const PRIVACY_POLICY_UPDATED_DATE = "19.08.2025";
  const { customer } = useCustomer();
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: updateResource,
  });

  if (!customer) {
    return null;
  }

  const consentForPolicyUpdateNeeded =
    !customer.privacyPolicyAcceptedDate || dayjs(customer.privacyPolicyAcceptedDate).isBefore(dayjs(PRIVACY_POLICY_UPDATED_DATE, "DD.MM.YYYY"));

  const consentForTermsOfServiceUpdateNeeded = false;
  // commented ToS Check so older users don't get a mixed message or agb changes, because their created is copied
  // to the termsOfServiceAcceptedDate so they might not have read the new agb's but thats fine atm

  // const consentForTermsOfServiceUpdateNeeded =
  //   !customer.termsOfServiceAcceptedDate || dayjs(customer.termsOfServiceAcceptedDate).isBefore(dayjs(TERMS_OF_SERVICE_UPDATED_DATE, "DD.MM.YYYY"));

  if (!consentForPolicyUpdateNeeded && !consentForTermsOfServiceUpdateNeeded) {
    return null;
  }

  const handleUpdateConsent = async () => {
    setIsLoading(true);
    let data: Record<string, string> = {};
    const today = dayjs().format("YYYY-MM-DD HH:mm:ss");

    if (consentForPolicyUpdateNeeded) {
      data.privacyPolicyAcceptedDate = today;
    }

    if (consentForTermsOfServiceUpdateNeeded) {
      data.termsOfServiceAcceptedDate = today;
    }

    await updateMutation.mutateAsync({
      uri: "/customers",
      id: customer.id,
      data,
    });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/customers"),
    });
    setIsLoading(false);
  };

  let title: string;
  let consentDescription: ReactNode;

  if (consentForTermsOfServiceUpdateNeeded && consentForPolicyUpdateNeeded) {
    title = "Aktualisierung unserer AGB und Datenschutzerklärung";
    consentDescription = (
      <>
        <Typography>Mit Klick auf „Weiter“:</Typography>
        <br />
        <MuiListNativeStyled
          listItems={[
            "stimmen Sie den aktualisierten AGB der Legalbird Rechtsanwälte GmbH und der Legalbird GmbH verbindlich zu,",
            "bestätigen Sie, dass Sie die Datenschutzerklärung zur Kenntnis genommen haben.",
          ]}
        />
        <br />
        <Typography>
          Falls Sie den neuen AGB nicht zustimmen möchten, bitten wir Sie, uns zu kontaktieren. Ohne Ihre Zustimmung können wir Ihnen leider unseren Service
          nicht weiter anbieten.
        </Typography>
      </>
    );
  } else if (consentForPolicyUpdateNeeded) {
    title = "Hinweis zur Aktualisierung unserer Datenschutzerklärung";
    consentDescription = (
      <>
        <Typography>Diese Hinweise dienen nur Ihrer Information. Klicken Sie auf “Weiter” um das Fenster zu schließen und fortzufahren.</Typography>
        <br />
        <Typography>Falls Sie Fragen zu den Änderungen haben, kontaktieren Sie uns gerne.</Typography>
      </>
    );
  } else {
    title = "Hinweis zur Aktualisierung unserer AGB";
    consentDescription = (
      <>
        <Typography>Mit dem Klick auf “Weiter” akzeptieren Sie die aktualisierten AGB’s.</Typography>
        <br />
        <Typography>
          Falls Sie den neuen AGB nicht zustimmen möchten, bitten wir Sie, uns zu kontaktieren. Ohne Ihre Zustimmung können wir Ihnen den Service nicht weiter
          anbieten.
        </Typography>
      </>
    );
  }

  return (
    <DialogModal open={true} title={title} responsive={true} maxWidth={"md"}>
      {consentForTermsOfServiceUpdateNeeded && <TermsOfServiceUpdateDetails />}
      {consentForPolicyUpdateNeeded && <PrivacyPolicyUpdateDetails />}
      {consentDescription}
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <ButtonLoading onClick={handleUpdateConsent} isLoading={isLoading} variant={"contained"} color={"primary"} sx={{ width: "280px", marginTop: "25px" }}>
          Weiter
        </ButtonLoading>
      </Box>
    </DialogModal>
  );
};
export default ConsentUpdateModal;
